'use client';

import { useCallback, useMemo, useState } from 'react';

import type { Listing, ListingVariant } from '@ps/models/lib/listing';

import {
  type VariantAttrType,
  type VariantSelection,
  type VariantValueType,
  attrValueOfVariant,
  kDefaultVariantAttr,
  kVariantAttrEmptyValue,
  resolveSelectedVariantId,
} from '../functions/variantCategories';

export interface UseVariantSelectionResult {
  selection: VariantSelection;
  selectedVariantId: string | undefined;
  selectedVariant: ListingVariant | undefined;
  onSelectAttr: (attr: VariantAttrType, value: VariantValueType) => void;
  /** True if the listing has any active variants (after `hidden` filter). */
  hasVariants: boolean;
}

/**
 * Mirrors RN `useMultiVariantFeature`. Owns the per-attribute selection map
 * (e.g. `{ Size: 'L', Color: 'Red' }`); clicking an already-selected value
 * clears it (RN parity). Auto-selects when the listing has exactly one
 * variant so the price/quantity reflect it without a click.
 *
 * RN's `onTrackVariantChange` / `onVariantAttrSelected` callbacks (used to
 * clear the "Select a variant" error + fire analytics) are intentionally
 * omitted — their consumers (Buy-click validation, analytics) live in the
 * deferred checkout flow.
 */
export function useVariantSelection(
  listing: Pick<Listing, 'variants' | 'skuAttributes'>,
): UseVariantSelectionResult {
  const activeVariants = useMemo(
    () => (listing.variants ?? []).filter(v => !v.hidden),
    [listing.variants],
  );
  const hasVariants = activeVariants.length > 0;

  const attrs = useMemo(
    () => Object.keys(listing.skuAttributes ?? {}),
    [listing.skuAttributes],
  );

  const [selection, setSelection] = useState<VariantSelection>(() => {
    // Auto-select when there's exactly one variant.
    if (activeVariants.length !== 1) return {};
    const only = activeVariants[0];
    const initial: VariantSelection = {};
    if (attrs.length > 0) {
      for (const attr of attrs) {
        initial[attr] = attrValueOfVariant(only, attr);
      }
    } else {
      // Legacy single-name variant.
      initial[kDefaultVariantAttr] = only.name;
    }
    return initial;
  });

  const onSelectAttr = useCallback(
    (attr: VariantAttrType, value: VariantValueType) => {
      setSelection(prev => {
        const next = { ...prev };
        // Clicking the already-selected value clears it (RN parity).
        next[attr] = next[attr] === value ? kVariantAttrEmptyValue : value;
        return next;
      });
    },
    [],
  );

  const selectedVariantId = useMemo(
    () => resolveSelectedVariantId(listing, selection),
    [listing, selection],
  );

  const selectedVariant = useMemo(
    () => activeVariants.find(v => v.id === selectedVariantId),
    [activeVariants, selectedVariantId],
  );

  return {
    selection,
    selectedVariantId,
    selectedVariant,
    onSelectAttr,
    hasVariants,
  };
}
